import { authHeader, config } from '_helpers';
import axios from 'axios';

export const layoffService = {
  getAccess,
  getLayoffs,
  getCandidates,
  getSigners,
  getPreview,
  sendLayoffs,
  remindLayoff,
  cancelLayoff,
  voidLayoff,
  resendEmail,
  downloadPdf,
  reassignSigner,
  getHistory,
  recordRefusal,
  getSignatures,
  getSignatureCount,
  getSignatureDetail,
  getSignaturePreview,
  signLayoff,
  recordRefusalAsSigner,
  getSavedSignature,
  getActiveNotice,
  getNotice,
  getNoticePreview,
  signNotice,
  getReview,
  getReviewPreview,
  saveReview,
  finalizeLayoff,
  getNotificationLists,
  saveNotificationLists,
};

const baseUrl = () => `${config.apiUrl}/api/layoffs`;

async function getAccess() {
  const { data } = await axios.get(`${baseUrl()}/access`, {
    headers: authHeader(),
  });
  return data.data;
}

async function getLayoffs(params) {
  const { data } = await axios.post(baseUrl(), params, {
    headers: authHeader(),
  });
  return data;
}

async function getCandidates(params) {
  const { data } = await axios.post(`${baseUrl()}/candidates`, params, {
    headers: authHeader(),
  });
  return data.data;
}

async function getSigners() {
  const { data } = await axios.get(`${baseUrl()}/signers`, {
    headers: authHeader(),
  });
  return data.data;
}

async function getPreview(employeeHash) {
  const { data } = await axios.get(`${baseUrl()}/preview`, {
    headers: authHeader(),
    params: employeeHash ? { employee_hash: employeeHash } : {},
    responseType: 'blob',
  });
  return data;
}

async function sendLayoffs(employees) {
  const { data } = await axios.post(
    `${baseUrl()}/send`,
    { employees },
    { headers: authHeader() },
  );
  return data;
}

async function remindLayoff(hash) {
  const { data } = await axios.post(
    `${baseUrl()}/${hash}/remind`,
    {},
    { headers: authHeader() },
  );
  return data;
}

async function cancelLayoff({ hash, reason }) {
  const { data } = await axios.post(
    `${baseUrl()}/${hash}/cancel`,
    { reason },
    { headers: authHeader() },
  );
  return data;
}

async function voidLayoff({ hash, reason }) {
  const { data } = await axios.post(
    `${baseUrl()}/${hash}/void`,
    { reason },
    { headers: authHeader() },
  );
  return data;
}

async function resendEmail(hash) {
  const { data } = await axios.post(
    `${baseUrl()}/${hash}/resend-email`,
    {},
    { headers: authHeader() },
  );
  return data;
}

// The finalized form as a file: { blob, filename }
async function downloadPdf({ hash, employeeName }) {
  const response = await axios.get(`${baseUrl()}/${hash}/pdf`, {
    headers: authHeader(),
    responseType: 'blob',
  });
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const fallback = `discharge-form-${(employeeName || hash)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}.pdf`;
  return { blob: response.data, filename: match ? match[1] : fallback };
}

async function reassignSigner({ hash, signerHash }) {
  const { data } = await axios.post(
    `${baseUrl()}/${hash}/reassign-signer`,
    { signer_hash: signerHash },
    { headers: authHeader() },
  );
  return data;
}

async function getHistory(hash) {
  const { data } = await axios.get(`${baseUrl()}/${hash}/history`, {
    headers: authHeader(),
  });
  return data.data;
}

async function recordRefusal({ hash, note }) {
  const { data } = await axios.post(
    `${baseUrl()}/${hash}/refuse`,
    { note },
    { headers: authHeader() },
  );
  return data;
}

// Superintendent side

async function getSignatures() {
  const { data } = await axios.get(`${baseUrl()}/signatures`, {
    headers: authHeader(),
  });
  return data.data;
}

async function getSignatureCount() {
  const { data } = await axios.get(`${baseUrl()}/signatures/count`, {
    headers: authHeader(),
  });
  return data.data.count;
}

async function getSignatureDetail(hash) {
  const { data } = await axios.get(`${baseUrl()}/signatures/${hash}`, {
    headers: authHeader(),
  });
  return data.data;
}

async function getSignaturePreview({ hash, draft }) {
  const { data } = await axios.post(
    `${baseUrl()}/signatures/${hash}/preview`,
    draft ?? {},
    { headers: authHeader(), responseType: 'blob' },
  );
  return data;
}

async function signLayoff({ hash, ...payload }) {
  const { data } = await axios.post(
    `${baseUrl()}/signatures/${hash}/sign`,
    payload,
    { headers: authHeader() },
  );
  return data;
}

async function recordRefusalAsSigner({ hash, note }) {
  const { data } = await axios.post(
    `${baseUrl()}/signatures/${hash}/refuse`,
    { note },
    { headers: authHeader() },
  );
  return data;
}

async function getSavedSignature() {
  const { data } = await axios.get(`${config.apiUrl}/api/user/signature`, {
    headers: authHeader(),
  });
  return data.data?.signature ?? null;
}

// Employee side

async function getActiveNotice() {
  const { data } = await axios.get(`${baseUrl()}/notice/active`, {
    headers: authHeader(),
  });
  return data.data;
}

async function getNotice(hash) {
  const { data } = await axios.get(`${baseUrl()}/notice/${hash}`, {
    headers: authHeader(),
  });
  return data.data;
}

async function getNoticePreview({ hash, draft }) {
  const { data } = await axios.post(
    `${baseUrl()}/notice/${hash}/preview`,
    draft ?? {},
    { headers: authHeader(), responseType: 'blob' },
  );
  return data;
}

async function signNotice({ hash, ...payload }) {
  const { data } = await axios.post(
    `${baseUrl()}/notice/${hash}/sign`,
    payload,
    { headers: authHeader() },
  );
  return data;
}

// Review by Super Admin / Payroll

async function getReview(hash) {
  const { data } = await axios.get(`${baseUrl()}/${hash}/review`, {
    headers: authHeader(),
  });
  return data.data;
}

async function getReviewPreview({ hash, draft }) {
  const { data } = await axios.post(
    `${baseUrl()}/${hash}/review/preview`,
    draft ?? {},
    { headers: authHeader(), responseType: 'blob' },
  );
  return data;
}

async function saveReview({ hash, ...payload }) {
  const { data } = await axios.post(`${baseUrl()}/${hash}/review`, payload, {
    headers: authHeader(),
  });
  return data;
}

async function finalizeLayoff({ hash, ...payload }) {
  const { data } = await axios.post(`${baseUrl()}/${hash}/finalize`, payload, {
    headers: authHeader(),
  });
  return data;
}

// Payroll and HR email lists (System Notifications settings)

async function getNotificationLists() {
  const { data } = await axios.get(`${baseUrl()}/notification-lists`, {
    headers: authHeader(),
  });
  return data.data;
}

async function saveNotificationLists(lists) {
  const { data } = await axios.post(`${baseUrl()}/notification-lists`, lists, {
    headers: authHeader(),
  });
  return data;
}
